// =============================================================
// leu/canonical.js
// Canonical, dependency-free LEU (Load Equivalent Unit) definitions.
//
// LEU is the universal capacity currency for LoadDash. This module is the
// single source of truth for the LEU numbers and the pure helpers that operate
// on them. It imports nothing, so it can never create a circular dependency;
// other modules (for example src/constants.js) re-export from here.
//
// Model (one unit end to end, no rescaling between layers):
//   1. A full standard truck bed equals the Full tier equals 6 LEU
//      (BED_LEU_STANDARD).
//   2. A job's size IS an LEU value: Small=1, Half=2, ThreeQ=3, Full=6.
//   3. Route, zone, and day capacities are plain SUMS of those same job LEU
//      values. There is no per-layer conversion factor.
//        ROUTE_LEU_BUDGET = one truck-day = BED_LEU_STANDARD * DUMP_CYCLES_PER_DAY
//        ZONE_MAX_LEU     = max summed job LEU per zone per service day
//        DAY_MAX_LEU      = max summed job LEU across all zones per service day
//
// Original IP: LoadDash Services LLC
// =============================================================

/** The only valid LEU job values, ascending. Note: 4 and 5 are NOT tiers. */
export const LEU_SET = Object.freeze([1, 2, 3, 6]);

/** Internal tier key to LEU job value. Keys are storage/runtime keys, not labels. */
export const TIER_LEU = Object.freeze({
  Small:  1,
  Half:   2,
  ThreeQ: 3,
  Full:   6,
});

/** A full standard truck bed equals the Full tier equals 6 LEU. */
export const BED_LEU_STANDARD = 6;

/**
 * LoadDash standard full-bed volume, in cubic feet (owner-approved baseline).
 *
 * This is the platform's NORMALIZATION baseline for turning a measured item
 * volume into a truck-bed fraction:
 *   bedFraction = measuredVolumeFt3 / STANDARD_BED_FT3
 * It is a LoadDash modeling constant, NOT a manufacturer-specific truck spec
 * and NOT a guaranteed physical bed size. Consumed only by the LiDAR
 * measurement helper (src/lidar/lidarMeasurement.js). snapToLEU() itself takes
 * the resulting fraction, never cubic feet.
 */
export const STANDARD_BED_FT3 = 54;

/** Dump runs a single truck completes in one service day. */
export const DUMP_CYCLES_PER_DAY = 3;

/**
 * Route planning budget for one truck for one service day, in LEU.
 * Pure sum of job LEU the truck can clear:
 * BED_LEU_STANDARD * DUMP_CYCLES_PER_DAY = 18.
 */
export const ROUTE_LEU_BUDGET = BED_LEU_STANDARD * DUMP_CYCLES_PER_DAY;

/** Max summed job LEU per zone per service day (DB-trigger enforced elsewhere). */
export const ZONE_MAX_LEU = 24;

/** Max summed job LEU across all zones per service day (DB-trigger enforced elsewhere). */
export const DAY_MAX_LEU = 48;

/**
 * Snap a measured truck-bed FRACTION to a canonical LEU tier value.
 *
 * Pure and side-effect free. NOT wired into any runtime (booking, photo/CV, or
 * LiDAR); provided for future use only.
 *
 * Input is a bed fraction (0 = empty, 1 = a full bed), NOT cubic feet.
 * Non-linear anchors map fraction to LEU, snapped at the midpoints between
 * anchors. Output is always one of LEU_SET ({1,2,3,6}); 4 and 5 are impossible.
 *
 *   anchors:   0.25 bed -> 1 | 0.50 bed -> 2 | 0.75 bed -> 3 | 1.00 bed -> 6
 *   midpoints:      0.375          0.625          0.875
 *
 * Fail-safe: invalid, negative, NaN, or non-finite input collapses to the
 * lowest tier (1), so a garbage reading can never upsell. A finite reading at
 * or above a full bed clamps to Full (6).
 *
 * @param {number} measuredFraction bed-fill fraction (0..1+, where 1 = full bed)
 * @returns {number} canonical LEU value, one of 1, 2, 3, 6
 */
export function snapToLEU(measuredFraction) {
  if (
    typeof measuredFraction !== "number" ||
    !Number.isFinite(measuredFraction) ||
    measuredFraction < 0
  ) {
    return TIER_LEU.Small; // 1 = lowest-tier fail-safe
  }
  if (measuredFraction < 0.375) return TIER_LEU.Small;  // ~0.25 bed -> 1
  if (measuredFraction < 0.625) return TIER_LEU.Half;   // ~0.50 bed -> 2
  if (measuredFraction < 0.875) return TIER_LEU.ThreeQ; // ~0.75 bed -> 3
  return TIER_LEU.Full;                                 // full or over-full -> 6
}
